import { pathOr } from 'ramda'
import {
  parsePhoneNumber,
} from 'libphonenumber-js'

import {
  F_GUEST_EMAIL,
  F_TITLE,
  F_FIRST_NAME,
  F_LAST_NAME,
  F_MOBILE,
  F_NATIONALITY,
  F_COUNTRY,
  F_ADDRESS,
  F_CITY,
  F_REGION,
  F_ZIP_CODE,
  F_TRIP_REASON,
  F_PAYMENT_METHOD,
  F_CARD_TYPE,
  F_CARD_HOLDER,
  F_CARD_NUMBER,
  F_CARD_EXPIRES,
  F_CARD_CVV,
} from '../../../secondStep/constants'

import {
  SEARCH_PARAM_CLIENT_CODE,
  SEARCH_PARAM_CONTRACT_NUMBER,
} from '../../../search/constants'

export const VALIDATOR_REQUIRED = 'required'
export const VALIDATOR_PHONE = 'phone'
export const VALIDATOR_EMAIL = 'email'
export const VALIDATOR_CARD_NUMBER = 'cardNumber'
export const VALIDATOR_CARD_NUMBER_15 = 'cardNumber15'
export const VALIDATOR_CARD_NUMBER_16 = 'cardNumber16'
export const VALIDATOR_EXPIRES = 'expires'
export const VALIDATOR_CVV = 'cvv'
export const VALIDATOR_CVV_3 = 'cvv3'
export const VALIDATOR_CVV_4 = 'cvv4'
export const VALIDATOR_CHARACTERS = 'onlyCharacters'

export const join = rules => (value, data) => rules
  .map(rule => rule(value, data))
  .filter(error => !!error)[0]

const invalidCardNumber = 'Invalid card number'
const invalidCVV = 'Invalid CVV'

/**
 * Validators translations
 */
const validatorsTexts = pathOr(
  {
    [VALIDATOR_REQUIRED]: 'Required',
    [VALIDATOR_PHONE]: 'The phone number is incorrect',
    [VALIDATOR_EMAIL]: 'Invalid email address',
    [VALIDATOR_CARD_NUMBER_15]: invalidCardNumber,
    [VALIDATOR_CARD_NUMBER_16]: invalidCardNumber,
    [VALIDATOR_CARD_NUMBER]: invalidCardNumber,
    [VALIDATOR_EXPIRES]: 'Invalid expires on',
    [VALIDATOR_CVV_3]: invalidCVV,
    [VALIDATOR_CVV_4]: invalidCVV,
    [F_GUEST_EMAIL]: 'Required F_GUEST_EMAIL',
    [F_TITLE]: 'Required F_TITLE',
    [F_FIRST_NAME]: 'Required F_FIRST_NAME',
    [F_LAST_NAME]: 'Required F_LAST_NAME',
    [F_MOBILE]: 'Required F_MOBILE',
    [F_NATIONALITY]: 'Required F_NATIONALITY',
    [F_COUNTRY]: 'Required F_COUNTRY',
    [F_ADDRESS]: 'Required F_ADDRESS',
    [F_CITY]: 'Required F_CITY',
    [F_REGION]: 'Required F_REGION',
    [F_ZIP_CODE]: 'Required F_ZIP_CODE',
    [F_TRIP_REASON]: 'Required F_TRIP_REASON',
    [F_PAYMENT_METHOD]: 'Required F_PAYMENT_METHOD',
    [F_CARD_TYPE]: 'Required F_CARD_TYPE',
    [F_CARD_HOLDER]: 'Required F_CARD_HOLDER',
    [F_CARD_NUMBER]: 'Required F_CARD_NUMBER',
    [F_CARD_EXPIRES]: 'Required F_CARD_EXPIRES',
    [F_CARD_CVV]: 'Required F_CARD_CVV',
    [SEARCH_PARAM_CONTRACT_NUMBER]: 'Required SEARCH_PARAM_CONTRACT_NUMBER',
  },
  ['drupalSettings', 'formControls', 'validators'],
)(window)

/**
 * Validators
 */
const requiredValidator = (value, fields, props, name) => (
  value ? undefined : (validatorsTexts[name] || validatorsTexts[VALIDATOR_REQUIRED])
)

const phoneValidator = (value) => {
  let validator
  if (value && value.length > 0) {
    try {
      const parsedNumber = parsePhoneNumber(value)

      if (!parsedNumber.isValid()) {
        validator = (validatorsTexts[VALIDATOR_PHONE])
      }
    } catch (e) {
      validator = (validatorsTexts[VALIDATOR_PHONE])
    }
  } else {
    validator = undefined
  }
  return validator
}

const emailValidator = (value, name) => (
  value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)
    ? (validatorsTexts[name] || validatorsTexts[VALIDATOR_EMAIL])
    : undefined
)

const cardNumber = (numLength, value) => (
  value && numLength && (value.length < numLength)
    ? validatorsTexts[VALIDATOR_CARD_NUMBER]
    : undefined
)

const cardNumber15 = value => cardNumber(15, value, VALIDATOR_CARD_NUMBER_15)

const cardNumber16 = value => cardNumber(16, value, VALIDATOR_CARD_NUMBER_16)

const cardExpires = (value) => {
  if (value) {
    const month = parseInt(value.substr(0, 2), 10)
    const year = parseInt(value.substr(2, 2), 10)
    const currentYear = parseInt(
      (new Date()).getFullYear().toString().substr(2, 2),
      10,
    )

    if (value.length < 4 || (month && month > 12) || (year < currentYear)) {
      return validatorsTexts[VALIDATOR_EXPIRES]
    }
    return undefined
  }
  return validatorsTexts.cardExpires
}

const cvvValidator = (cvvLength, value, name) => (
  value && cvvLength && (value.length < cvvLength)
    ? validatorsTexts[name]
    : undefined
)

const cvvCode3 = value => cvvValidator(3, value, VALIDATOR_CVV_3)

const cvvCode4 = value => cvvValidator(4, value, VALIDATOR_CVV_4)

const contractNumber = (value, allValues) => (
  allValues[SEARCH_PARAM_CLIENT_CODE]
  && !value
    ? validatorsTexts[SEARCH_PARAM_CONTRACT_NUMBER] : undefined
)

const onlyCharacters = (value, fields, props, name) => {
  if (!value || !/^[a-zA-ZàáâäôöóéèëêïîíñçùûüúÿæœßÀÁÂÄÔÖÓÉÈËÊÏÎÍÑŸÇÙÛÜÚÆŒẞ\s-]+$/.test(value)) {
    return (validatorsTexts[name] || validatorsTexts[VALIDATOR_REQUIRED])
  }
  return undefined
}

export const coreValidators = {
  [VALIDATOR_REQUIRED]: requiredValidator,
  [VALIDATOR_PHONE]: phoneValidator,
  [VALIDATOR_EMAIL]: emailValidator,
  [VALIDATOR_CARD_NUMBER_15]: cardNumber15,
  [VALIDATOR_CARD_NUMBER_16]: cardNumber16,
  [VALIDATOR_EXPIRES]: cardExpires,
  [VALIDATOR_CVV_3]: cvvCode3,
  [VALIDATOR_CVV_4]: cvvCode4,
  [SEARCH_PARAM_CONTRACT_NUMBER]: contractNumber,
  [VALIDATOR_CHARACTERS]: onlyCharacters,
}

export function createValidator(rules) {
  return (data = {}) => {
    const errors = {}
    Object.keys(rules).forEach((key) => {
      const rule = join([].concat(rules[key]))
      const error = rule(data[key], key, data)
      if (error) {
        errors[key] = error
      }
    })
    return errors
  }
}
